import * as React from "react";
import {
    Routes,
    Route,
    Navigate,
} from "react-router-dom";

import {AuthProvider} from "./AuthUser";
import AppAuthProviders from "./providers/AppAuthProviders";
import RequireAuth from "./RequireAuth";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./Home";
import DNAProfile from "./DNAProfile";
import Pricing from "./Pricing";

import HomeLayout from "./layouts/HomeLayout";
import AuthLayout from "./layouts/AuthLayout";
import DefaultLayout from "./layouts/DefaultLayout";
import SubscriptionLayout from "./layouts/SubscriptionLayout";
import PricingLayout from "./layouts/PricingLayout";

import Signup from "./pages/Auth/Signup";
import Signin from "./pages/Auth/Signin";
import ForgotPassword from "./pages/Auth/ForgotPassword";

import {VideoListPage, VideoPage} from "./features/Video";
import {ReviewsPage} from "./features/Review";

import Premier from "./pages/Subscriptions/Premier";
import PremierPlus from "./pages/Subscriptions/PremierPlus";
import Pro from "./pages/Subscriptions/Pro";

import SupportCenter from "./pages/Support/SupportCenter";
import SupportAnswerPage from "./pages/Support/SupportAnswerPage";

import UploadVideos from "./pages/UploadVideos";
import Account from "./pages/Account";
import Shipping from "./pages/Wizard/Shipping";
import About from "./pages/Wizard/About";
import About2 from "./pages/Wizard/About2";
import Upload from "./pages/Wizard/Upload";
import Consult from "./pages/Wizard/Consult";
import ResetPassword from "./pages/Auth/PasswordReset";
import Thankyou from "./pages/Subscriptions/Thankyou";
import Basic from "./pages/Subscriptions/Basic";
import InstructorLessons from "./pages/Admin/Lessons";
import InstructorLesson from "./pages/Admin/UploadVideos";
import AdminLayout from "./layouts/AdminLayout";

import List from "./pages/Videos/List";
import CreateVideo from "./pages/Admin/CreateVideo";
import VideosList from "./pages/Admin/Videos";
import EditVideo from "./pages/Admin/EditVideo";
import CreateCategory from "./pages/Admin/CreateCategory";
import EditCategory from "./pages/Admin/EditCategory";
import CategoryList from "./pages/Admin/Categories";
import CreateFAQCategory from "./pages/Admin/CreateFAQCategory";
import EditFAQCategory from "./pages/Admin/EditFAQCategory";
import FAQCategoryList from "./pages/Admin/FAQCategories";
import CreateFAQ from "./pages/Admin/CreateFAQ";
import EditFAQ from "./pages/Admin/EditFAQ";
import FAQList from "./pages/Admin/FAQ";
import MyLessons from "./pages/MyLessons";
import ProgressTracker from "./pages/ProgressTracker";
import Lesson from "./pages/Lesson";
import LessonComments from "./pages/Admin/LessonComments";
import AdminUsers from "./pages/Admin/Users";
import AdminUser from "./pages/Admin/User";
import EditUser from "./pages/Admin/EditUser";
import CreateUser from "./pages/Admin/CreateUser";
import CreateLesson from "./pages/Admin/CreateLesson";
import Cancel from "./pages/Subscriptions/Cancel";
import CancelReally from "./pages/Subscriptions/CancelReally";
import UpdatePayment from "./pages/Subscriptions/UpdatePayment";
import Plan from "./pages/Subscriptions/Plan";
import InstructorPricing from "./InstructorPricing";
import EditPlan from "./pages/Admin/EditPlan";
import Plans from "./pages/Admin/Plans";
import CreatePlan from "./pages/Admin/CreatePlan";
import GenerateOfferLink from "./pages/Admin/GenerateOfferLink";
import OfferLinks from "./pages/Admin/OfferLinks";
import Offer from "./pages/Offer";
import Welcome from "./pages/Welcome";
import Clinic from "./pages/Clinic";
import FreeSwingReview from "./pages/FreeSwingReview";
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import WorkWithChris from "./pages/WorkWithChris";

export default function App() {
  return (
    <AppAuthProviders>
        <ScrollToTop />
        <Routes>
          <Route element={<HomeLayout />}>
          <Route path="/" element={<Home />} />
        </Route>
          <Route element={<DefaultLayout />}>
              <Route path="/support" element={<SupportCenter />} />

              {/* Drawer route — opens the answer inside the SupportCenter drawer */}
              <Route path="/support/q/:id" element={<SupportCenter />} />

              {/* Full article standalone page (SEO + sharable article view) */}
              <Route path="/support/a/:id" element={<SupportAnswerPage />} />
        </Route>
        <Route element={<DefaultLayout />}>
          <Route path="/policies/privacy" element={<PrivacyPolicy />} />
            <Route path="/policies/terms" element={<TermsOfService />} />
        </Route>
          <Route element={<DefaultLayout />}>
          <Route path="/clinics/dual-force-golf-school" element={<Clinic />} />
        </Route>
          <Route element={<HomeLayout />}>
          <Route path="/free" element={<FreeSwingReview />} />
        </Route>
        <Route element={<DefaultLayout />}>
          <Route
            path="/account"
            element={
              <RequireAuth>
                <Account />
              </RequireAuth>
            }
          />
          <Route
            path="/dna-profile"
            element={
              <RequireAuth>
                <DNAProfile />
              </RequireAuth>
            }
          >
            <Route index element={<MyLessons />}/>
            <Route
              path="progress-tracker"
              element={<ProgressTracker />}
            />
          </Route>
          <Route
            path="/reviews"
            element={
              <RequireAuth>
                <ReviewsPage />
              </RequireAuth>
            }
          />
          {/* Old "lesson reviewed" emails link to /lessons, which never
              existed as a user route (MGDWD-164). */}
          <Route path="/lessons" element={<Navigate to="/reviews" replace />} />
            <Route
            path="/lesson/:lessonId"
            element={
              <RequireAuth>
                <Lesson />
              </RequireAuth>
            }
          />
          <Route
            path="/upload-videos"
            element={
              <RequireAuth>
                <UploadVideos />
              </RequireAuth>
            }
          />
          <Route
            path="/videos/"
            element={
                <VideoListPage />
            }
          />
          <Route
            path="/videos/:slug"
            element={
                <VideoPage />
            }
          />
        </Route>

        <Route element={<AdminLayout />}>
          <Route
            path="/admin/lessons"
            element={
              <RequireAuth>
                <InstructorLessons />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequireAuth>
                <AdminUsers />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <RequireAuth>
                <AdminUser />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/users/edit/:userId"
            element={
              <RequireAuth>
                <EditUser />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/users/create"
            element={
              <RequireAuth>
                <CreateUser />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/users/:userId/createLesson"
            element={
              <RequireAuth>
                <CreateLesson />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/lessons/:lessonId"
            element={
              <RequireAuth>
                <InstructorLesson />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/videos"
            element={
              <RequireAuth>
                <VideosList />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/videos/create"
            element={
              <RequireAuth>
                <CreateVideo />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/videos/:videoId"
            element={
              <RequireAuth>
                <EditVideo />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/category"
            element={
              <RequireAuth>
                <CategoryList />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/category/create"
            element={
              <RequireAuth>
                <CreateCategory />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/category/:categoryId"
            element={
              <RequireAuth>
                <EditCategory />
              </RequireAuth>
            }
          />
            <Route
                path="/admin/faq"
                element={
                    <RequireAuth>
                        <FAQList />
                    </RequireAuth>
                }
            />
            <Route
                path="/admin/faq/create"
                element={
                    <RequireAuth>
                        <CreateFAQ />
                    </RequireAuth>
                }
            />
            <Route
                path="/admin/faq/:id"
                element={
                    <RequireAuth>
                        <EditFAQ />
                    </RequireAuth>
                }
            />

            <Route
                path="/admin/faq/category"
                element={
                    <RequireAuth>
                        <FAQCategoryList />
                    </RequireAuth>
                }
            />
            <Route
                path="/admin/faq/category/create"
                element={
                    <RequireAuth>
                        <CreateFAQCategory />
                    </RequireAuth>
                }
            />
            <Route
                path="/admin/faq/category/:categoryId"
                element={
                    <RequireAuth>
                        <EditFAQCategory />
                    </RequireAuth>
                }
            />
          <Route
            path="/admin/lessonComments"
            element={
              <RequireAuth>
                <LessonComments />
              </RequireAuth>
            }
          />
            <Route
            path="/admin/plan/:planId"
            element={
              <RequireAuth>
                <EditPlan />
              </RequireAuth>
            }
          />
            <Route
            path="/admin/plans"
            element={
              <RequireAuth>
                <Plans />
              </RequireAuth>
            }
          />
            <Route
            path="/admin/plans/create"
            element={
              <RequireAuth>
                <CreatePlan />
              </RequireAuth>
            }
          />
            <Route
            path="/admin/discount-offers"
            element={
              <RequireAuth>
                <OfferLinks />
              </RequireAuth>
            }
          />
            <Route
            path="/admin/discount-offers/new"
            element={
              <RequireAuth>
                <GenerateOfferLink />
              </RequireAuth>
            }
          />
        </Route>

        <Route element={<SubscriptionLayout/>}>
          <Route
            path="/thankyou"
            element={
              <RequireAuth>
                <Thankyou />
              </RequireAuth>
            }
          />
          <Route
            path="/cancel"
            element={
              <RequireAuth>
                <Cancel />
              </RequireAuth>
            }
          />
          <Route
            path="/update-payment"
            element={
              <RequireAuth>
                <UpdatePayment />
              </RequireAuth>
            }
          />
          <Route
            path="/cancel-sub"
            element={
              <RequireAuth>
                <CancelReally />
              </RequireAuth>
            }
          />
          <Route
            path="/wizard/shipping"
            element={
              <RequireAuth>
                <Shipping />
              </RequireAuth>
            }
          />
          <Route
            path="/wizard/about"
            element={
              <RequireAuth>
                <About2 />
              </RequireAuth>
            }
          />
          <Route
            path="/wizard/about2"
            element={
              <RequireAuth>
                <About />
              </RequireAuth>
            }
          />
          <Route
            path="/wizard/upload"
            element={
              <RequireAuth>
                <Upload />
              </RequireAuth>
            }
          />
          <Route
            path="/wizard/consult"
            element={
              <RequireAuth>
                <Consult />
              </RequireAuth>
            }
          />
        </Route>
        <Route element={<SubscriptionLayout />}>
          <Route path="/premier" element={<Premier />} />
          <Route path="/premier-plus" element={<PremierPlus />} />
          <Route path="/pro" element={<Pro />} />
          <Route path="/basic" element={<Basic />} />
          <Route path="/plans/:planId" element={<Plan />} />
            <Route path="/welcome" element={<Welcome />} />
        </Route>
         <Route element={<PricingLayout backgroundColor="#fff" />}>

            {/* Canonical pricing URL — renders directly (no redirect flash).
                /pricing/instructor/15 kept working below for existing links
                (e.g. the video Chris already published references it). */}
            <Route path="/pricing" element={<InstructorPricing />} />
          <Route path="/pricing/instructor/:instructorId" element={<InstructorPricing />} />
          <Route path="/work-with-chris" element={<WorkWithChris />} />
          <Route path="/offer" element={<Offer />} />
        </Route>
        <Route element={<AuthLayout backgroundColor="#1B2631" />}>
            <Route path="/signin" element={<Signin />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>
        <Route element={<HomeLayout/>} >
          <Route path='*' element={<Home />}/>
        </Route>
      </Routes>
    </AppAuthProviders>
  );
}
